/**
 * T3 CARD SETUP — the pure model behind the Card readers section (SUNMI T3 only).
 * Spec: docs/agenticpos-rubytill/rubytill-pay.md → "T3 card setup".
 * Mirrors `RegisterCardSetupSchema` / `cardSetupIssues` in @platform/shared-types;
 * the T3 test uses the shared helper when the dev server's pre-bundled
 * shared-types has it, and the identical local rule when it does not.
 */
import * as SharedTypes from '@platform/shared-types';

export interface CardSetupDraft {
  profile: 'sunmi_t3';
  tapToPayEnabled: boolean;
  primary: 'tap_to_pay' | 'reader';
  tapToPayEmvDeviceId: string | null;
  readerEmvDeviceId: string | null;
  showBackupAlways: boolean;
  offerBackupOnFail: boolean;
  startOnBackupWhenTapOffline: boolean;
  debitToBackup: boolean;
}

const sharedIsT3 = (SharedTypes as any)?.isSunmiT3Model as ((m: unknown) => boolean) | undefined;

/** Keys on `appProfile.installProfileId === 'sunmi_t3'` — never the display name. */
export function isSunmiT3Model(model: { appProfile?: unknown } | null | undefined): boolean {
  if (typeof sharedIsT3 === 'function') return sharedIsT3(model);
  return !!model && (model.appProfile as { installProfileId?: unknown } | undefined)?.installProfileId === 'sunmi_t3';
}

export const READER_CLASSES = ['dejavoo_spin_usb', 'dejavoo_spin_cloud'];
export const isTapDevice = (d: any) => String(d?.deviceClass || '') === 'dejavoo_rubytill_pay'
  && (d?.rtpayCardEngine === 'tap_to_pay' || d?.rtpay?.cardEngine === 'tap_to_pay' || d?.model === 'Android Tap to Pay');
export const isReaderDevice = (d: any) => READER_CLASSES.includes(String(d?.deviceClass || ''));
const usable = (d: any) => d?.status !== 'inactive' && d?.status !== 'deactivated';

/** A fresh setup: Tap to Pay primary on the location's one tap device (if any), no backup. */
export function defaultCardSetup(devices: any[], stored?: any): CardSetupDraft {
  if (stored && typeof stored === 'object') {
    return {
      profile: 'sunmi_t3',
      tapToPayEnabled: stored.tapToPayEnabled !== false,
      primary: stored.primary === 'reader' ? 'reader' : 'tap_to_pay',
      tapToPayEmvDeviceId: stored.tapToPayEmvDeviceId || null,
      readerEmvDeviceId: stored.readerEmvDeviceId || null,
      showBackupAlways: stored.showBackupAlways !== false,
      offerBackupOnFail: stored.offerBackupOnFail !== false,
      startOnBackupWhenTapOffline: stored.startOnBackupWhenTapOffline !== false,
      debitToBackup: stored.debitToBackup === true,
    };
  }
  const tap = devices.filter((d) => isTapDevice(d) && usable(d));
  return {
    profile: 'sunmi_t3', tapToPayEnabled: true, primary: 'tap_to_pay',
    tapToPayEmvDeviceId: tap.length === 1 ? tap[0].deviceId : null,
    readerEmvDeviceId: null,
    showBackupAlways: true, offerBackupOnFail: true, startOnBackupWhenTapOffline: true, debitToBackup: false,
  };
}

/** Flip the Tap to Pay switch: off ⇒ the reader becomes primary; on ⇒ Tap to Pay is primary again. */
export function setTapToPay(d: CardSetupDraft, on: boolean): CardSetupDraft {
  return { ...d, tapToPayEnabled: on, primary: on ? 'tap_to_pay' : 'reader' };
}

/** The same invariants the server enforces — a non-empty list disables Save. */
export function cardSetupProblems(d: CardSetupDraft): string[] {
  const out: string[] = [];
  if (d.tapToPayEnabled && d.primary === 'tap_to_pay' && !d.tapToPayEmvDeviceId) out.push('Pick the Android Tap to Pay device.');
  if ((!d.tapToPayEnabled || d.primary === 'reader') && !d.readerEmvDeviceId) out.push('With Tap to Pay off, pick the card reader that becomes primary.');
  if (d.tapToPayEmvDeviceId && d.readerEmvDeviceId && d.tapToPayEmvDeviceId === d.readerEmvDeviceId) out.push('Primary and backup must be different devices.');
  return out;
}

export const sameSetup = (a: CardSetupDraft | null, b: CardSetupDraft | null) => JSON.stringify(a) === JSON.stringify(b);

/** "••••4471" — never the full TPN / merchant code on screen. */
export const masked = (v: unknown) => {
  const s = String(v || '').trim();
  return s ? `••••${s.slice(-4)}` : '—';
};

/** Fleet line from heartbeat detail keys sent by a T3 till. Null when the till sent none. */
export function cardReadersLine(detail: Record<string, unknown> | null | undefined): { text: string; tone: 'ok' | 'warn' | 'bad' } | null {
  if (!detail) return null;
  /* Card setup (sunmi-card-setup.md): a till on the primary/backup shape reports
     `primaryState` (+ `primaryKind`, `primaryAppVersion`) and `backupState`
     (+ `backupKind`). Read that first; the earlier ttp* keys follow unchanged. */
  if (typeof detail.primaryState === 'string') return primaryBackupLine(detail);
  const ttp = typeof detail.ttpState === 'string' ? detail.ttpState : null;
  const backup = typeof detail.backupState === 'string' ? detail.backupState : null;
  if (!ttp && !backup) return null;
  const TTP: Record<string, string> = {
    ready: 'Tap to Pay ready', not_paired: 'Tap to Pay not paired', offline: 'Tap to Pay offline',
    location_off: 'Tap to Pay — location off', disabled: 'Tap to Pay off',
  };
  const BK: Record<string, string> = { ready: 'backup ready', offline: 'backup offline', none: 'no backup' };
  const v = typeof detail.ttpAppVersion === 'string' && detail.ttpAppVersion ? ` v${detail.ttpAppVersion}` : '';
  const parts = [ttp ? `${TTP[ttp] || `Tap to Pay ${ttp}`}${v}` : null, backup ? (BK[backup] || `backup ${backup}`) : null].filter(Boolean);
  const bad = (ttp && ttp !== 'ready' && ttp !== 'disabled') && backup !== 'ready';
  const warn = (ttp && ttp !== 'ready' && ttp !== 'disabled') || backup === 'offline';
  return { text: parts.join(' · '), tone: bad ? 'bad' : warn ? 'warn' : 'ok' };
}

const KIND_SHORT: Record<string, string> = {
  tap_to_pay: 'Tap to Pay', dejavoo_p: 'P17', magtek_ped: 'MagTek PED', magtek_go: 'MagTek Go', sunbay_tapro: 'RubyTill Pay CPad',
};
const STATE_WORD: Record<string, string> = {
  ready: 'ready', offline: 'offline', not_paired: 'not paired', location_off: 'location off', disabled: 'off', none: 'none',
};

/** "Primary Tap to Pay ready v1.2.0 · backup P17 ready" — from the primary/backup heartbeat keys. */
function primaryBackupLine(d: Record<string, unknown>): { text: string; tone: 'ok' | 'warn' | 'bad' } {
  const str = (k: string) => (typeof d[k] === 'string' && d[k] ? (d[k] as string) : null);
  const p = str('primaryState') as string;
  const b = str('backupState');
  const pk = str('primaryKind'); const bk = str('backupKind');
  const v = str('primaryAppVersion') || str('ttpAppVersion');
  const primary = `Primary${pk ? ` ${KIND_SHORT[pk] || pk}` : ''} ${STATE_WORD[p] || p}${v ? ` v${v}` : ''}`;
  const backup = !b || b === 'none' ? 'no backup' : `backup${bk ? ` ${KIND_SHORT[bk] || bk}` : ''} ${STATE_WORD[b] || b}`;
  const primaryDown = p !== 'ready';
  const tone = primaryDown && b !== 'ready' ? 'bad' : primaryDown || b === 'offline' ? 'warn' : 'ok';
  return { text: `${primary} · ${backup}`, tone };
}
